import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PlayCircle, Globe2, Clock3, Lock, Gavel } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLES = {
    available: "text-emerald-300 border-emerald-400/40 bg-emerald-400/5",
    under_offer: "text-platinum border-platinum/40 bg-platinum/5",
    sold: "text-silver-300 border-silver-300/30 bg-white/5",
};

export default function FilmCard({ film, index = 0 }) {
    const { user } = useAuth();
    const status = film.rights_status || "available";
    const statusLabel =
        status === "under_offer"
            ? "Under Offer"
            : status === "sold"
              ? "Sold"
              : "Available";

    const ytThumb = film.trailer_youtube_id
        ? `https://i.ytimg.com/vi/${film.trailer_youtube_id}/hqdefault.jpg`
        : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: Math.min(index, 10) * 0.05 }}
            whileHover={{ scale: 1.04, y: -4 }}
            className="group relative"
            data-testid={`film-card-${film.id}`}
        >
            <Link to={`/film/${film.id}`} className="block" data-testid={`film-card-link-${film.id}`}>
                <div className="relative aspect-[2/3] overflow-hidden rounded-sm border border-white/5 bg-ink-100">
                    {film.poster_url ? (
                        <img
                            src={film.poster_url}
                            alt={film.title}
                            className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-0"
                            loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full bg-ink-100 flex items-center justify-center">
                            <span className="font-display text-silver-500 text-xl">
                                {film.title}
                            </span>
                        </div>
                    )}
                    {ytThumb && (
                        <img
                            src={ytThumb}
                            alt={`${film.title} trailer`}
                            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                            loading="lazy"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-90 pointer-events-none" />

                    <div className="absolute top-3 left-3">
                        <span
                            className={`text-[10px] tracking-[0.18em] uppercase border px-2 py-1 rounded-sm backdrop-blur-sm ${STATUS_STYLES[status]}`}
                        >
                            {statusLabel}
                        </span>
                    </div>
                    {film.auction_enabled && (
                        <div className="absolute top-3 right-3" data-testid={`auction-badge-${film.id}`}>
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-platinum text-platinum bg-ink-400/80 backdrop-blur-sm px-2 py-1 rounded-sm">
                                <Gavel className="w-3 h-3" /> Auction
                            </span>
                        </div>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-1 group-hover:translate-y-0 transition-transform">
                        <div className="flex items-center gap-2 text-[11px] text-silver-300/80 uppercase tracking-widest mb-1">
                            <Globe2 className="w-3 h-3 text-platinum" />
                            {film.country} • {film.language}
                        </div>
                        <h3 className="font-display text-2xl text-white leading-tight">
                            {film.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-silver-400 text-xs">
                            <span className="flex items-center gap-1">
                                <Clock3 className="w-3 h-3" /> {film.runtime}m
                            </span>
                            {user?.role === "buyer" || user?.role === "admin" || user?.role === "creator" ? (
                                <span className="text-platinum/80">
                                    ${Number(film.price_usd || 0).toLocaleString()}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 text-silver-500 italic">
                                    <Lock className="w-3 h-3" /> Sign in for price
                                </span>
                            )}
                        </div>

                        <div className="mt-3 flex items-center gap-2 text-platinum opacity-0 group-hover:opacity-100 transition-opacity">
                            <PlayCircle className="w-4 h-4" />
                            <span className="text-xs tracking-widest uppercase">Preview</span>
                        </div>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}
